import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Logged in successfully!');
      } else {
        await register(formData.email, formData.password, formData.name, formData.phone);
        toast.success('Account created successfully!');
      }
      const from = location.state?.from?.pathname || '/';
      navigate(from);
    } catch (error) {
      const message = error.response?.data?.detail || (isLogin ? 'Login failed' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white border border-[#E5E2DC] p-8"
      >
        <h1 data-testid="auth-heading" className="text-3xl font-light tracking-tight mb-2">
          {isLogin ? 'Welcome Back' : 'Create Account'}
        </h1>
        <p className="text-sm text-[#7a5e75] mb-8">
          {isLogin ? 'Login to your account' : 'Sign up to start shopping'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <div>
              <label htmlFor="name" className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                data-testid="name-input"
                value={formData.name}
                onChange={handleInputChange}
                required={!isLogin}
                className="input-underline w-full"
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              data-testid="email-input"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="input-underline w-full"
            />
          </div>

          <div>
            <label htmlFor="password" className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              data-testid="password-input"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              className="input-underline w-full"
            />
          </div>

          {!isLogin && (
            <div>
              <label htmlFor="phone" className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                data-testid="phone-input"
                value={formData.phone}
                onChange={handleInputChange}
                className="input-underline w-full"
              />
            </div>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            data-testid="toggle-auth-mode-button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-[#7a5e75] hover:text-[#641b4d] transition-colors"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;